import { useEffect, useState } from "react";
import {
  IconPencil,
  IconCheck,
  IconCamera,
  IconUserCircle,
} from "@tabler/icons-react";
import { COUNTRY_OPTIONS } from "./data";
import styles from "../../../../pages/ClientDashboard/Profile/profile.module.css";
import Input, { Select } from "../../FormInput/input";
import Button from "../../Button/button";
import { convertToBase64, getNameInitials } from "../../../../utils/functions";
import {
  updateClientProfile,
  uploadClientProfilePhoto,
} from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import PhoneInput from "react-phone-number-input";

export default function BasicInfoCard({
  info,
  onShowToast,
  getCompanyProfile,
}) {
  const [editing, setEditing] = useState(false);
  const [load, setLoad] = useState(false);
  const [file, setFile] = useState(null);
  const [clientID, setClientId] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (info) {
      setForm({
        firstName: info?.firstName,
        lastName: info?.lastName,
        email: info?.email,
        phone: info?.phone,
      });
      setClientId(info?._id);
    }
  }, [info]);

  const initials =
    info && info?.firstName && info?.lastName
      ? getNameInitials(info?.firstName, info?.lastName)
      : "";

  function startEdit() {
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setLoad(true);
      if (profilePic && file && clientID) {
        const base64Image = await convertToBase64(file);
        const data = {
          avatar: base64Image,
        };
        const res = await uploadClientProfilePhoto(clientID, data);
      }
      const res = await updateClientProfile(form);
      if (res?.data?.success) {
        onShowToast("Changes saved successfully");
        getCompanyProfile();
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
      setEditing(false);
    }
  };

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconUserCircle size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>Basic Information</div>
            <div className={styles.cardSubtitle}>
              Your name, contact details and location
            </div>
          </div>
        </div>
        <div className={styles.btnGroup}>
          {!editing && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconPencil size={14} />}
              onClick={startEdit}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className={styles.cardBody}>
        {/* Avatar row */}
        <div className={styles.avatarSection}>
          <div
            className={`${styles.avatarPreview} ${editing ? styles.avatarPreviewEditing : ""}`}
          >
            {initials}
            <div className={styles.avatarOverlay}>
              <IconCamera size={20} />
              <span>Change</span>
            </div>
          </div>
          <div className={styles.avatarInfo}>
            <h4>
              {info?.firstName} {info?.lastName}
            </h4>
            <p>JPG, PNG or GIF — max 2 MB. Square image recommended.</p>
          </div>
        </div>

        {!editing ? (
          <div className={styles.infoGrid}>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>First Name</div>
              <div className={styles.infoValue}>{info?.firstName}</div>
            </div>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>Last Name</div>
              <div className={styles.infoValue}>{info?.lastName}</div>
            </div>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>Work Email</div>
              <div className={styles.infoValue}>{info?.email}</div>
            </div>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>Phone Number</div>
              <div className={styles.infoValue}>
                {info?.countryCode} {info?.phone}
              </div>
            </div>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>Country</div>
              <div className={styles.infoValue}>{info?.country}</div>
            </div>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>City</div>
              <div className={styles.infoValue}>{info?.city}</div>
            </div>
          </div>
        ) : (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldRow}>
              <Input
                label="First Name"
                id="e-firstName"
                type="text"
                value={form?.firstName}
                onChange={setField("firstName")}
              />
              <Input
                label="Last Name"
                id="e-lastName"
                type="text"
                value={form?.lastName}
                onChange={setField("lastName")}
              />
            </div>
            <Input
              label="Work Email"
              id="e-email"
              type="email"
              value={form?.email}
              onChange={setField("email")}
            />
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>Phone Number</label>
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="NG"
                className="flex border border-[#e2e8f4] !px-3 !py-2 !text-sm !text-[#0d1b3e] rounded-lg"
                value={form?.phone}
                onChange={(e) =>
                  setForm((prevState) => ({
                    ...prevState,
                    phone: e,
                  }))
                }
              />
            </div>
            {/* <div className={styles.fieldRow}>
              <Select
                label="Country"
                id="e-country"
                value={form.country}
                onChange={setField("country")}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
              <Input
                label="City"
                id="e-city"
                type="text"
                value={form.city}
                onChange={setField("city")}
              />
            </div> */}
            <div className={styles.editActions}>
              <Button
                variant="secondary"
                leftIcon={<IconCheck size={15} />}
                onClick={handleSave}
                loading={load}
                disabled={load}
              >
                Save Changes
              </Button>
              <Button
                variant="outlineBlue"
                onClick={cancelEdit}
                disabled={load}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
